import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * Page layout (CO-88, audit G2, N1). Every page below the header is a `Page`
 * with one `PageHeader`: breadcrumbs above the title on every page below
 * the organisation level, a 24/600 title, one line of description, an
 * optional meta row, and actions on the right, at most one of them primary.
 * The header-to-first-section gap is fixed here, so no page sets its own
 * heading margins.
 */

export function Page({
  width = 'default',
  className,
  children,
  testId,
}: {
  /** "narrow" (760 px) is for one form, wizard or onboarding flow. */
  width?: 'default' | 'narrow';
  className?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className={`page${width === 'narrow' ? ' page--narrow' : ''}${className ? ` ${className}` : ''}`} data-testid={testId}>
      {children}
    </div>
  );
}

export interface Crumb {
  label: ReactNode;
  /** Omitted for the current page, which is the last crumb. */
  to?: string;
}

/** Where "up" is. The last crumb is the current page and is not a link. */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  return (
    <nav className={`breadcrumbs${className ? ` ${className}` : ''}`} aria-label="Breadcrumb">
      <ol>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={index}>
              {item.to && !last ? <Link to={item.to}>{item.label}</Link> : <span aria-current={last ? 'page' : undefined}>{item.label}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  meta,
  actions,
  titleId,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Pages below the organisation level: the path up, ending with this page. */
  breadcrumbs?: Crumb[];
  /** Short facts or status badges under the title. */
  meta?: ReactNode;
  actions?: ReactNode;
  titleId?: string;
}) {
  return (
    <header className="page-header">
      {breadcrumbs && breadcrumbs.length > 0 ? <Breadcrumbs items={breadcrumbs} /> : null}
      <div className="page-header__row">
        <div className="page-header__text">
          <h1 id={titleId} className="page-header__title">
            {title}
          </h1>
          {description ? <p className="page-header__description">{description}</p> : null}
          {meta ? <div className="page-header__meta">{meta}</div> : null}
        </div>
        {actions ? <div className="page-header__actions">{actions}</div> : null}
      </div>
    </header>
  );
}
